import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const SCRIPT_ID = "cloudflare-turnstile-script";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

let scriptPromise;

function loadTurnstile() {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(SCRIPT_ID);

    const handleLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error("Turnstile API unavailable"));
      }
    };
    const handleError = () => reject(new Error("Turnstile script failed to load"));

    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
    }

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });

    if (!script.parentNode) {
      document.head.appendChild(script);
    }
  }).catch((error) => {
    scriptPromise = undefined;
    throw error;
  });

  return scriptPromise;
}

const messages = {
  it: {
    prompt: "Verifica antispam",
    expired: "La verifica è scaduta: completala di nuovo.",
    error: "La verifica antispam non è disponibile. Ricarica la pagina e riprova.",
    missingKey: "La verifica antispam non è configurata.",
  },
  en: {
    prompt: "Spam protection",
    expired: "The verification has expired. Please complete it again.",
    error: "Spam protection is unavailable. Reload the page and try again.",
    missingKey: "Spam protection is not configured.",
  },
};

const Turnstile = forwardRef(function Turnstile(
  { action, language, onTokenChange },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const [errorType, setErrorType] = useState("");
  const copy = messages[language] || messages.it;

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useImperativeHandle(ref, () => ({
    reset() {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
      setErrorType("");
      onTokenChangeRef.current("");
    },
  }), []);

  useEffect(() => {
    let cancelled = false;

    setErrorType("");
    onTokenChangeRef.current("");

    if (!SITE_KEY) {
      setErrorType("missingKey");
      return undefined;
    }

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) {
          return;
        }

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action,
          language: language === "en" ? "en" : "it",
          theme: "light",
          callback: (token) => {
            setErrorType("");
            onTokenChangeRef.current(token);
          },
          "expired-callback": () => {
            setErrorType("expired");
            onTokenChangeRef.current("");
            turnstile.reset(widgetIdRef.current);
          },
          "timeout-callback": () => {
            setErrorType("expired");
            onTokenChangeRef.current("");
            turnstile.reset(widgetIdRef.current);
          },
          "error-callback": () => {
            setErrorType("error");
            onTokenChangeRef.current("");
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setErrorType("error");
          onTokenChangeRef.current("");
        }
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [action, language]);

  return (
    <div className="turnstile-field">
      <span className="small-text turnstile-label">{copy.prompt}</span>
      <div ref={containerRef} className="turnstile-widget" />
      {errorType && (
        <span className="small-text contact-status-error" role="status">
          {copy[errorType]}
        </span>
      )}
    </div>
  );
});

export default Turnstile;
